import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem("cookieConsent");
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setShowBanner(false);
  };

  const declineCookies = () => {
    localStorage.setItem("cookieConsent", "declined");
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-6xl mx-auto bg-white border border-[#E1DCCE] rounded-2xl shadow-[0_1px_2px_rgba(52,44,26,.06),0_4px_14px_rgba(52,44,26,.05),0_12px_32px_rgba(52,44,26,.10)]">
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                {/* Cookie Icon */}
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-[#E8B44C] rounded-xl flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-[#221A06]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                      />
                    </svg>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-bold text-[#26313B] mb-2">
                    We use cookies
                  </h3>
                  <p className="text-[#3D4B59] text-sm md:text-base leading-relaxed">
                    We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content and ads.
                    By clicking "Accept", you consent to our use of cookies. Learn more in our{" "}
                    <Link
                      to="/privacy-policy"
                      className="text-[#A8720E] hover:text-[#7C540A] underline"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <button
                    onClick={declineCookies}
                    className="px-6 py-3 bg-white hover:bg-[#F1EEE5] border border-[#E1DCCE] text-[#3D4B59] font-medium rounded-lg transition-all duration-200"
                  >
                    Decline
                  </button>
                  <button
                    onClick={acceptCookies}
                    className="px-6 py-3 bg-[#E8B44C] hover:bg-[#DFA93C] border border-[#D9A32E] text-[#221A06] font-semibold rounded-lg shadow-lg shadow-[#E8B44C]/30 transition-all duration-200"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
